import type { DirectiveBinding } from 'vue'
import { formatNumberWithUnit } from '@/utils'

function applyFormatting(el: HTMLElement, binding: DirectiveBinding) {
  const { value, modifiers, arg: precision = '2' } = binding
  const {
    unit: unitAfter,
    amount,
    emptyPlaceholder = '--',
    unitBefore,
    locale,
    unitFontSize, // 新增：单位字体大小
    unitColor, // 新增：单位颜色
  } = value

  const useThousandSeparator = modifiers.separator ?? true
  const profitColor = modifiers.profitColor ?? false

  const numberValue = parseFloat(amount)
  const precisionNumber = parseInt(precision)

  if (Number.isNaN(numberValue)) {
    el.innerHTML = emptyPlaceholder
    return
  }

  try {
    // 使用 Intl.NumberFormat 进行本地化格式化
    // 优先使用传入的 locale，否则使用浏览器默认语言
    const userLocale = locale || navigator.language || 'en-US'

    const formatter = new Intl.NumberFormat(userLocale, {
      minimumFractionDigits: Number.isNaN(precisionNumber) ? 2 : precisionNumber,
      maximumFractionDigits: Number.isNaN(precisionNumber) ? 2 : precisionNumber,
      useGrouping: useThousandSeparator, // 是否使用千位分隔符
    })

    const formattedNumber = formatter.format(numberValue)

    // 构建 HTML 内容，支持单位的独立样式
    let content = formattedNumber

    // 处理前缀单位
    if (unitBefore) {
      const unitStyle = buildUnitStyle(unitFontSize, unitColor)
      content = `<span style="${unitStyle}">${unitBefore}</span>${content}`
    }

    // 处理后缀单位
    if (unitAfter) {
      const unitStyle = buildUnitStyle(unitFontSize, unitColor)
      content = `<span>${content}</span><span style="${unitStyle}"> ${unitAfter}</span>`
    }

    el.innerHTML = content

    // 应用盈亏颜色（只影响数字部分，不影响单位）
    if (profitColor) {
      const numberSpan = el.querySelector('span') ? el.childNodes : [el]
      const color = getColorForValue(numberValue)

      // 如果有单位，只给非单位的文本节点设置颜色
      if (unitBefore || unitAfter) {
        el.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const wrapper = document.createElement('span')
            wrapper.style.color = color
            wrapper.textContent = node.textContent
            el.replaceChild(wrapper, node)
          }
        })
      } else {
        el.style.color = color
      }
    }
  } catch (error) {
    // 如果本地化格式化失败，回退到原来的格式化方式
    const formattedNumber = formatNumberWithUnit(
      numberValue,
      Number.isNaN(precisionNumber) ? 2 : precisionNumber,
      useThousandSeparator,
      unitBefore,
      unitAfter,
    )

    // 即使回退也要支持单位样式
    if ((unitBefore || unitAfter) && (unitFontSize || unitColor)) {
      const parts = formattedNumber.split(' ')
      const numberPart = parts[0]
      const unitPart = parts[1]

      if (unitPart) {
        const unitStyle = buildUnitStyle(unitFontSize, unitColor)
        el.innerHTML = `${numberPart}<span style="${unitStyle}"> ${unitPart}</span>`
      } else {
        el.textContent = formattedNumber
      }
    } else {
      el.textContent = formattedNumber
    }

    if (profitColor) {
      const color = getColorForValue(numberValue)
      if (unitBefore || unitAfter) {
        el.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const wrapper = document.createElement('span')
            wrapper.style.color = color
            wrapper.textContent = node.textContent
            el.replaceChild(wrapper, node)
          }
        })
      } else {
        el.style.color = color
      }
    }
  }
}

// 构建单位样式
function buildUnitStyle(fontSize?: string, color?: string): string {
  const styles: string[] = []

  if (fontSize) {
    styles.push(`font-size: ${fontSize}`)
  }

  if (color) {
    styles.push(`color: ${color}`)
  }

  return styles.join('; ')
}

// 获取盈亏颜色
function getColorForValue(value: number): string {
  if (value > 0) {
    return 'var(--profit)'
  } else if (value < 0) {
    return 'var(--loss)'
  } else {
    return 'var(--ink-primary)'
  }
}

export default {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    applyFormatting(el, binding)
  },
  updated(el: HTMLElement, binding: DirectiveBinding) {
    applyFormatting(el, binding)
  },
}
